import { Capacitor } from '@capacitor/core';
import { settings } from './prefs';

/** Vrai dans l'APK, faux dans un navigateur (PWA comprise). */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/** La barre d'état d'Android, accordée au ciel du jeu. Importée à la demande : le plugin n'a
 *  rien à faire dans le bundle web. */
export async function applyChrome(): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#4b2bd6' });
  } catch {
    /* plugin absent : la barre garde la couleur de capacitor.config.ts */
  }
}

/** Une vibration brève quand un mot est trouvé — Android uniquement, et jamais bloquante. */
export async function buzz(): Promise<void> {
  if (!isNative() || !settings().haptics) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch {
    /* pas de vibreur, ou plugin absent */
  }
}

/**
 * Le bouton Retour d'Android. Sans écouteur, Capacitor le laisse au WebView, qui remonte
 * l'historique — et le jeu n'en a pas : l'application se fermait au milieu d'une partie. Ici, le
 * jeu décide : `onBack` rend `true` s'il a consommé le geste (refermer la partie, la fin de
 * partie, un panneau) ; sinon, sur le chemin, on MET EN ARRIÈRE-PLAN plutôt que de quitter —
 * c'est ce qu'Android fait pour une application qu'on retrouve où on l'a laissée.
 */
export async function onBackButton(onBack: () => boolean): Promise<void> {
  if (!isNative()) return;
  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('backButton', () => {
      if (!onBack()) void App.minimizeApp();
    });
  } catch {
    /* plugin absent : le Retour reste celui du WebView */
  }
}

/**
 * Ouvre le sélecteur de partage du système, ou à défaut copie le texte. Trois chemins, comme dans
 * Codigrid (src/platform.ts là-bas) : dans l'APK, `navigator.share` N'EXISTE PAS — la WebView de
 * Capacitor ne l'expose pas —, d'où @capacitor/share (aucune permission Android) ; sur le web,
 * `navigator.share` fait le même travail ; sur un navigateur de bureau, il reste le presse-papiers.
 * Importé à la demande : le plugin n'a rien à faire dans le bundle web.
 */
export async function shareText(o: {
  title: string;
  text: string;
  url: string;
}): Promise<'partagé' | 'copié' | 'échec'> {
  try {
    if (isNative()) {
      const { Share } = await import('@capacitor/share');
      await Share.share(o);
      return 'partagé';
    }
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      await nav.share(o);
      return 'partagé';
    }
    await navigator.clipboard.writeText(`${o.text}\n${o.url}`);
    return 'copié';
  } catch {
    // Un partage annulé par le joueur passe aussi par ici : rien à lui dire d'un geste abandonné.
    return 'échec';
  }
}
